// Shared schemas for the kernel's value types.
//
// The single most important property of this file: every schema OUTPUTS a real
// kernel type, produced by the kernel's own constructor. `capacitySchema` does not
// merely check "integer >= 1" and hand back a `number` — it calls the kernel's
// `capacity()`, so there is exactly one definition of what a capacity is. If the
// kernel tightens the rule, this schema tightens with it and the build tells us.
//
// That is what makes contracts a decoupler rather than a second source of truth:
// a UI agent and a backend agent can work the same feature concurrently, and drift
// between them shows up as a type error rather than as an integration bug.

import {
  capacity,
  revision,
  type AvailabilityBatchId,
  type AvailabilityWindowId,
  type Capacity,
  type CircleId,
  type CommitmentId,
  type DirectRequestId,
  type EventId,
  type HelpMode,
  type Importance,
  type Instant,
  type NeedType,
  type PersonId,
  type Revision,
  type SupportNeedId,
} from "@herefor/domain-kernel";
import { z } from "zod";

// FIRST, and deliberately a side-effect import (bead hf-vvj9). Zod reads `jitless`
// when a schema is CONSTRUCTED, and every schema below is built at module scope, so
// this has to have run before this file's body does. Every other schema module in the
// package imports this one, which is what makes that true everywhere.
import "./schemaCompilation.js";

/**
 * An instant, as epoch milliseconds.
 *
 * The kernel brands `Instant` but exposes no constructor, so the brand is applied
 * here after the same integer check the kernel's own arithmetic assumes.
 */
export const instantSchema: z.ZodType<Instant> = z
  .number()
  .int("A time must be whole milliseconds since the epoch.")
  .transform((value) => value as Instant);

/** A revision counter (EVT-007, NED-008). Non-negative integer, via the kernel. */
export const revisionSchema: z.ZodType<Revision> = z.number().transform((value, ctx) => {
  try {
    return revision(value);
  } catch (error) {
    ctx.addIssue({ code: "custom", message: (error as Error).message });
    return z.NEVER;
  }
});

/** How many people (NED-004, COV-006). At least 1, via the kernel's own rule. */
export const capacitySchema: z.ZodType<Capacity> = z.number().transform((value, ctx) => {
  try {
    return capacity(value);
  } catch (error) {
    ctx.addIssue({ code: "custom", message: (error as Error).message });
    return z.NEVER;
  }
});

/**
 * Identifiers.
 *
 * Deliberately opaque strings: the UI must never parse meaning out of an id, and
 * share links expose only `publicPlanRef` (plan §16.18). Each brand gets its own
 * schema so a PersonId cannot be passed where a NeedId belongs.
 */
const idSchema = z.string().min(1, "An identifier must not be empty.");

export const circleIdSchema = idSchema.transform((v) => v as CircleId);
export const personIdSchema = idSchema.transform((v) => v as PersonId);
export const eventIdSchema = idSchema.transform((v) => v as EventId);
export const supportNeedIdSchema = idSchema.transform((v) => v as SupportNeedId);
export const availabilityBatchIdSchema = idSchema.transform((v) => v as AvailabilityBatchId);
export const availabilityWindowIdSchema = idSchema.transform((v) => v as AvailabilityWindowId);
export const directRequestIdSchema = idSchema.transform((v) => v as DirectRequestId);
export const commitmentIdSchema = idSchema.transform((v) => v as CommitmentId);

/**
 * Enumerations, spelled out rather than derived from the kernel's union types.
 *
 * A zod enum needs runtime values and the kernel exports these as TYPES only, so the
 * literals are repeated here — which is a drift risk, and therefore covered by an
 * exhaustiveness check in primitives.test.ts that fails to compile if the kernel adds
 * a member this list is missing.
 */
export const needTypeSchema = z.enum([
  "visit",
  "drive",
  "accompany",
  "call",
  "errand",
  "other",
]) satisfies z.ZodType<NeedType>;

export const helpModeSchema = z.enum([
  "in_person",
  "driving",
  "phone_or_online",
]) satisfies z.ZodType<HelpMode>;

export const importanceSchema = z.enum([
  "ordinary",
  "important",
  "critical",
]) satisfies z.ZodType<Importance>;

/**
 * A help window.
 *
 * Only the shape is checked here — that it is two instants in order. Whether the
 * window is LEGAL (AVL-007: no crossing a local day boundary, never split) depends on
 * the circle's timezone, which a schema does not have. That check belongs to the
 * kernel's `validateWindow`, and the boundary calls it; see the rejection union in
 * ./errors.ts, which mirrors the kernel's typed reasons exactly.
 */
export const windowSchema = z
  .object({ startsAt: instantSchema, endsAt: instantSchema })
  .refine((w) => w.endsAt > w.startsAt, {
    message: "A help window must end after it starts.",
    path: ["endsAt"],
  });

export type WindowInput = z.input<typeof windowSchema>;
export type WindowOutput = z.output<typeof windowSchema>;
